import { useCallback, useEffect, useMemo, useState } from "react";
import { useToast } from "@/components/ToastProvider";
import { fetchEmailLogs, sendEmail } from "@/api/email";
import { createDraftEmail, favoriteDraftEmail, fetchTemplates } from "@/api/crm";
import type { EmailLog, EmailTemplateOption, SendEmailPayload } from "@/types/email";
import type { EmailTemplate as CrmEmailTemplate, OutboundEmail } from "@/types/crm";

type UseEmailClientArgs = {
  contactId?: number | null;
  leadId?: number | null;
  /** Load legacy email_logs history (owner-only /email/logs). Off for musician outreach. */
  includeLogs?: boolean;
};

const flattenTemplates = (records: CrmEmailTemplate[]): EmailTemplateOption[] =>
  records.flatMap((template) =>
    (template.versions ?? [])
      .filter((version) => Boolean(version.subject_template) && Boolean(version.body_template))
      .map((version) => ({
        id: `crm-template-${template.id}-v${version.id}`,
        template_id: template.id,
        version_id: version.id,
        name: template.name,
        channel: template.channel,
        persona: template.persona,
        subject: version.subject_template ?? "",
        body_html: version.body_template ?? "",
        metadata: version.metadata ?? {},
        active: version.active,
      })),
  );

export function useEmailClient(args: UseEmailClientArgs = {}) {
  const { contactId, leadId, includeLogs = true } = args;
  const { push } = useToast();
  const [templates, setTemplates] = useState<EmailTemplateOption[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [logs, setLogs] = useState<EmailLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const refreshTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    try {
      const data = await fetchTemplates(true);
      setTemplates(flattenTemplates(data ?? []));
    } catch (error) {
      console.warn("Unable to load email templates", error);
      push({ title: "Email templates", message: error instanceof Error ? error.message : "Failed to load templates." });
    } finally {
      setTemplatesLoading(false);
    }
  }, [push]);

  const refreshLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const payload = await fetchEmailLogs({
        contact_id: contactId ?? undefined,
        lead_id: leadId ?? undefined,
      });
      setLogs(payload ?? []);
    } catch (error) {
      console.warn("Unable to load email logs", error);
    } finally {
      setLogsLoading(false);
    }
  }, [contactId, leadId]);

  useEffect(() => {
    void refreshTemplates();
  }, [refreshTemplates]);

  useEffect(() => {
    if (includeLogs) void refreshLogs();
  }, [includeLogs, refreshLogs]);

  const handleSend = useCallback(
    async (payload: SendEmailPayload) => {
      if (!payload.to) {
        push({ title: "Missing recipient", message: "Add an email address before sending." });
        return;
      }
      setSending(true);
      try {
        const log = await sendEmail(payload);
        push({ title: "Email sent", message: `Message queued to ${payload.to}` });
        await refreshLogs();
        return log;
      } catch (error) {
        push({ title: "Send failed", message: error instanceof Error ? error.message : "Unable to send email." });
        throw error;
      } finally {
        setSending(false);
      }
    },
    [push, refreshLogs],
  );

  const handleTest = useCallback(
    async (payload: SendEmailPayload) => {
      if (!payload.to) {
        push({ title: "Missing recipient", message: "Add an email address before sending." });
        return;
      }
      try {
        await sendEmail(payload);
        push({ title: "Email sent", message: `Message queued to ${payload.to}` });
        await refreshLogs();
      } catch (error) {
        push({ title: "Send failed", message: error instanceof Error ? error.message : "Unable to send email." });
      }
    },
    [push, refreshLogs],
  );

  const handleGenerateDraft = useCallback(
    async (payload: { lead_id: number; campaign_id?: number | null; template_id?: number | null }) => {
      try {
        const draft = await createDraftEmail(payload);
        push({ title: "Draft created", message: "LLM generated a new draft email." });
        return draft;
      } catch (error) {
        push({ title: "Draft failed", message: error instanceof Error ? error.message : "Unable to generate draft." });
        throw error;
      }
    },
    [push],
  );

  const handleFavoriteDraft = useCallback(
    async (emailId: number, payload: { template_id?: number | null; name?: string | null; persona?: string | null; channel?: string | null; notes?: string | null }) => {
      try {
        const template = await favoriteDraftEmail(emailId, payload);
        push({ title: "Template saved", message: template.name });
        await refreshTemplates();
        return template;
      } catch (error) {
        push({ title: "Save failed", message: error instanceof Error ? error.message : "Unable to save template." });
        throw error;
      }
    },
    [push, refreshTemplates],
  );

  return useMemo(
    () => ({
      templates,
      templatesLoading,
      logs,
      logsLoading,
      sending,
      sendEmail: handleSend,
      sendTest: handleTest,
      generateDraft: handleGenerateDraft,
      favoriteDraft: handleFavoriteDraft,
      refreshTemplates,
      refreshLogs,
    }),
    [templates, templatesLoading, logs, logsLoading, sending, handleSend, handleTest, handleGenerateDraft, handleFavoriteDraft, refreshTemplates, refreshLogs],
  );
}
